/**
 * PartnerAccountingPage Component
 * 협력사 출금제한 페이지
 */
import React, { useState, useMemo, useCallback } from 'react';
import { usePartnerData } from '../hooks/usePartnerData';
import { useAccountingUtils } from '../hooks/useAccountingUtils';
import { useAccountManagementTab } from '../hooks/useAccountManagementTab';
import { useTableSort, SortConfig } from '@/shared/hooks/useTableSort';
import SortIcon from '@/shared/components/dataDisplay/SortIcon';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import Loading from '@/shared/components/feedback/Loading';
import { PartnerListItem } from '../types/AccountingPartner.types';
import { SUPPLIER_STATUS_OPTIONS } from '@/shared/constants/statusCodes';
import { formatDateToDisplay } from '@/shared/utils/formatter';
import RiderAccountingPage from './RiderAccountingPage';
import '@/shared/styles/search-form.css';
import '../styles/AccountingManagement.css';

// 테이블 헤더 정의
const TABLE_HEADERS = [
  { key: 'suppName', label: '협력사명', sortable: false },
  { key: 'statusCode', label: '운영상태', sortable: false },
  { key: 'suppLvlName', label: '등급', sortable: true },
  { key: 'suppId', label: '협력사아이디', sortable: false },
  { key: 'insDate', label: '등록일자', sortable: true },
  { key: 'endMngDay', label: '운영종료일자', sortable: true },
  { key: 'balAmt', label: '계좌잔액', sortable: true },
  { key: 'unpaidAmt', label: '미납금', sortable: true },
  { key: 'action', label: '계좌관리', sortable: false }
] as const;

/**
 * 페이지 헤더 컴포넌트
 */
interface PageHeaderProps {
  userType: string;
  activeTab: string;
  onTabChange: (tab: any) => void;
}

const PageHeader: React.FC<PageHeaderProps> = ({ userType, activeTab, onTabChange }) => (
  <div className="page-header">
    <div className="page-title-container">
      <h1 className="page-title">출금제한</h1>
      {userType === 'HEADQUARTERS' && (
        <div className="title-tabs">
          <button
            className={`title-tab ${activeTab === 'partner' ? 'active' : ''}`}
            onClick={() => onTabChange('partner')}
          >
            협력사 출금제한
          </button>
          <button
            className={`title-tab ${activeTab === 'rider' ? 'active' : ''}`}
            onClick={() => onTabChange('rider')}
          >
            라이더 출금제한
          </button>
        </div>
      )}
    </div>
  </div>
);

/**
 * 검색 폼 컴포넌트
 */
interface SearchFormProps {
  searchPartnerId: string;
  setSearchPartnerId: (value: string) => void;
  searchPartnerIdInput: string;
  setSearchPartnerIdInput: (value: string) => void;
  searchStatus: string;
  setSearchStatus: (value: string) => void;
  searchWithdrawalStatus: string; // 출금상태 필터 추가
  setSearchWithdrawalStatus: (value: string) => void; // 출금상태 필터 setter 추가
  partnerNames: Array<{ suppId: string; suppName: string }>;
  loading: boolean;
  onSearch: () => void;
}

const SearchForm: React.FC<SearchFormProps> = ({
  searchPartnerId,
  setSearchPartnerId,
  searchPartnerIdInput,
  setSearchPartnerIdInput,
  searchStatus,
  setSearchStatus,
  searchWithdrawalStatus, // 출금상태 필터 추가
  setSearchWithdrawalStatus, // 출금상태 필터 setter 추가
  partnerNames,
  loading,
  onSearch
}) => {
  // 협력사 옵션을 SelectOption 형식으로 변환
  const partnerOptions: SelectOption[] = useMemo(() => {
    return partnerNames.map((partner) => ({
      value: partner.suppId,
      label: partner.suppName
    }));
  }, [partnerNames]);

  return (
    <div className="card search-card">
      <div className="card-body">
        <div className="search-form partner-accounting-search-form">
          {/* 협력사명 */}
          <div className="form-group">
            <div className="form-field">
              <label className="form-label">
                <i className="fas fa-building" />
                협력사명
              </label>
              <SearchableSelect
                options={partnerOptions}
                value={searchPartnerId}
                onChange={(value) => setSearchPartnerId(value)}
                placeholder="협력사 검색..."
                showAllOption={true}
                allOptionLabel="전체"
              />
            </div>
          </div>

          {/* 협력사 아이디 */}
          <div className="form-group">
            <div className="form-field">
              <label className="form-label">
                <i className="fas fa-id-card" />
                협력사 아이디
              </label>
              <input
                type="text"
                className="text-input"
                value={searchPartnerIdInput}
                onChange={(e) => setSearchPartnerIdInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && onSearch()}
                placeholder="협력사 아이디 입력..."
              />
            </div>
          </div>

          {/* 운영 상태 */}
          <div className="form-group">
            <div className="form-field">
              <label className="form-label">
                <i className="fas fa-toggle-on" />
                운영 상태
              </label>
              <div className="status-select-wrapper">
                <select
                  className="status-select"
                  value={searchStatus}
                  onChange={(e) => setSearchStatus(e.target.value)}
                >
                  {SUPPLIER_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 출금상태 */}
          <div className="form-group">
            <div className="form-field">
              <label className="form-label">
                <i className="fas fa-money-bill-wave" />
                출금상태
              </label>
              <div className="status-select-wrapper">
                <select
                  className="status-select"
                  value={searchWithdrawalStatus}
                  onChange={(e) => setSearchWithdrawalStatus(e.target.value)}
                >
                  <option value="">전체</option>
                  <option value="N">출금허용</option>
                  <option value="Y">출금제한</option>
                </select>
              </div>
            </div>
          </div>

          {/* 버튼 그룹 */}
          <div className="form-group button-group">
            <button
              className="btn btn-primary"
              onClick={onSearch}
              disabled={loading}
            >
              <i className="fas fa-search" />
              조회
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 계좌 데이터 테이블 컴포넌트
 */
interface AccountTableProps {
  partnerData: any[];
  formatNumber: (value: number | null | undefined) => string;
  getStatusBadge: (statusCode: string) => string;
  getGradeBadgeClass: (gradeName: string | null | undefined) => string;
  getStatusBadgeClass: (statusCode: string) => string;
  getPartnerRowClassName: (statusCode: string, balAmt: number | null | undefined) => string;
  isPartnerAccountDisabled: (statusCode: string) => boolean;
  handleSendMoney: (partnerName: string, amount: string, suppId?: string, riderId?: string) => void;
  handleWithdrawalLimit: (suppName: string, suppId: string, withdrawalYn: string, targetAction: 'ALLOW' | 'RESTRICT') => void;
  sortConfig: SortConfig;
  onSort: (columnKey: string) => void;
}

const AccountTable: React.FC<AccountTableProps> = ({
  partnerData,
  formatNumber,
  getStatusBadge,
  getGradeBadgeClass,
  getStatusBadgeClass,
  getPartnerRowClassName,
  isPartnerAccountDisabled,
  handleSendMoney,
  handleWithdrawalLimit,
  sortConfig,
  onSort
}) => {
  return (
    <div className="table-container">
      <table className="table sortable-table">
        <thead>
          <tr>
            {TABLE_HEADERS.map(header => {
              const isSorted = sortConfig.columnKey === header.key;
              const direction = isSorted ? sortConfig.direction : null;
              return (
                <th
                  key={header.key}
                  className={`${header.sortable ? 'sortable-header' : ''} ${isSorted ? 'sorted' : ''}`}
                  onClick={() => header.sortable && onSort(header.key)}
                >
                  {header.label}
                  {header.sortable && <SortIcon direction={direction} />}
                </th>
              );
            })}
          </tr>
        </thead>
      <tbody>
        {partnerData.length === 0 ? (
          <tr>
            <td colSpan={9} className="text-center">
              등록된 협력사가 없습니다.
            </td>
          </tr>
        ) : (
          partnerData.map((account) => {
            const isTerminated = isPartnerAccountDisabled(account.statusCode);

            return (
              <tr key={account.suppId} className={getPartnerRowClassName(account.statusCode, account.balAmt)}>
                <td>{account.suppName}</td>
                <td>
                  <span className={`badge ${getStatusBadgeClass(account.statusCode)}`}>
                    {account.statusCodeName || getStatusBadge(account.statusCode)}
                  </span>
                </td>
                <td>
                  {account.suppLvlName ? (
                    <span className={`badge ${getGradeBadgeClass(account.suppLvlName)}`}>
                      {account.suppLvlName}
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
                <td>{account.suppId}</td>
                <td>{formatDateToDisplay(account.insDate)}</td>
                <td>{formatDateToDisplay(account.endMngDay)}</td>
                <td className="number-cell">
                  {account.balAmt !== null && account.balAmt !== undefined ? formatNumber(account.balAmt) : ''}
                </td>
                <td className="number-cell">
                  {account.unpaidAmt !== null && account.unpaidAmt !== undefined ? formatNumber(account.unpaidAmt) : ''}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className={`btn btn-send ${isTerminated ? 'disabled' : ''}`}
                      onClick={() => !isTerminated && handleSendMoney(account.suppName, '-', account.suppId)}
                      disabled={isTerminated}
                    >
                      일차감
                    </button>
                    {/* 출금허용 버튼 - 현재 출금허용(N) 상태면 색상 표시, 클릭 불가 */}
                    <button
                      className={`btn btn-limit allow ${account.withdrawalYn === 'N' ? 'active' : 'inactive'}`}
                      onClick={() => !isTerminated && (account.withdrawalYn === 'Y' || account.withdrawalYn === null || account.withdrawalYn === undefined) && handleWithdrawalLimit(account.suppName, account.suppId, account.withdrawalYn, 'ALLOW')}
                      disabled={isTerminated || account.withdrawalYn === 'N'}
                    >
                      출금허용
                    </button>
                    {/* 출금제한 버튼 - 현재 출금제한(Y) 상태면 색상 표시, 클릭 불가 */}
                    <button
                      className={`btn btn-limit restrict ${account.withdrawalYn === 'Y' ? 'active' : 'inactive'}`}
                      onClick={() => !isTerminated && (account.withdrawalYn === 'N' || account.withdrawalYn === null || account.withdrawalYn === undefined) && handleWithdrawalLimit(account.suppName, account.suppId, account.withdrawalYn, 'RESTRICT')}
                      disabled={isTerminated || account.withdrawalYn === 'Y'}
                    >
                      출금제한
                    </button>
                  </div>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
      </table>
    </div>
  );
};

/**
 * 메인 컴포넌트
 */
const PartnerAccountingPage: React.FC = () => {
  // 데이터 관리 훅
  const {
    searchPartnerId,
    setSearchPartnerId,
    searchPartnerIdInput,
    setSearchPartnerIdInput,
    searchStatus,
    setSearchStatus,
    searchWithdrawalStatus, // 출금상태 필터 추가
    setSearchWithdrawalStatus, // 출금상태 필터 setter 추가
    allData,
    loading,
    partnerNames,
    itemsPerPage,
    handleSearch,
    handleWithdrawalLimit,
    fetchPartnerList,
    resetSearchConditions
  } = usePartnerData();

  // 유틸리티 함수
  const {
    getStatusBadge,
    formatNumber,
    handleSendMoney,
    getGradeBadgeClass,
    getStatusBadgeClass,
    getPartnerRowClassName,
    isPartnerAccountDisabled
  } = useAccountingUtils();

  // 탭 관리
  const { activeTab, userType, handleTabChange } = useAccountManagementTab(() => {
    resetSearchConditions();
    fetchPartnerList();
  });

  // 정렬 훅 사용
  const {
    sortConfig,
    sortedData: sortedAllData,
    handleSort: onSort,
    resetSort
  } = useTableSort<PartnerListItem>({
    data: allData || [],
    numericColumns: ['balAmt', 'unpaidAmt'],
    dateColumns: ['insDate', 'endMngDay'],
    customOrderColumns: {
      suppLvlName: ['마스터', '다이아', '골드', '실버', '브론즈', '등급없음']
    }
  });

  // 페이지 상태
  const [currentPage, setCurrentPage] = useState(1);

  // 정렬 핸들러 (정렬 시 첫 페이지로 이동)
  const handleSort = useCallback((columnKey: string) => {
    onSort(columnKey);
    setCurrentPage(1);
  }, [onSort]);

  // 페이지네이션 적용된 현재 페이지 데이터
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedAllData.slice(startIndex, endIndex);
  }, [sortedAllData, currentPage, itemsPerPage]);

  // 전체 페이지 수
  const totalPages = useMemo(() => {
    return Math.ceil(sortedAllData.length / itemsPerPage);
  }, [sortedAllData.length, itemsPerPage]);

  // 페이지 변경 핸들러
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // 검색 시 첫 페이지로 리셋
  const handleSearchWithReset = useCallback(() => {
    setCurrentPage(1);
    handleSearch();
  }, [handleSearch]);

  // 로딩 중
  if (loading) {
    return (
      <div className="account-management">
        <PageHeader
          userType={userType}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <div className="loading-container">
          <Loading message="협력사 정보를 불러오는 중..." />
        </div>
      </div>
    );
  }

  // 라이더 탭
  if (activeTab === 'rider') {
    return (
      <div className="account-management">
        <PageHeader
          userType={userType}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <RiderAccountingPage />
      </div>
    );
  }

  // 협력사 탭 (메인)
  return (
    <div className="account-management">
      <PageHeader
        userType={userType}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      <SearchForm
        searchPartnerId={searchPartnerId}
        setSearchPartnerId={setSearchPartnerId}
        searchPartnerIdInput={searchPartnerIdInput}
        setSearchPartnerIdInput={setSearchPartnerIdInput}
        searchStatus={searchStatus}
        setSearchStatus={setSearchStatus}
        searchWithdrawalStatus={searchWithdrawalStatus} // 출금상태 필터 추가
        setSearchWithdrawalStatus={setSearchWithdrawalStatus} // 출금상태 필터 setter 추가
        partnerNames={partnerNames}
        loading={loading}
        onSearch={handleSearchWithReset}
      />

      <div className="card">
        <div className="card-body">
          <AccountTable
            partnerData={currentPageData}
            formatNumber={formatNumber}
            getStatusBadge={getStatusBadge}
            getGradeBadgeClass={getGradeBadgeClass}
            getStatusBadgeClass={getStatusBadgeClass}
            getPartnerRowClassName={getPartnerRowClassName}
            isPartnerAccountDisabled={isPartnerAccountDisabled}
            handleSendMoney={handleSendMoney}
            handleWithdrawalLimit={handleWithdrawalLimit}
            sortConfig={sortConfig}
            onSort={handleSort}
          />

          {totalPages > 1 && (
            <div className="pagination-container">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                maxPagesToShow={5}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerAccountingPage;
